import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';
import type { InvestorTerms } from '@pointfive/contracts';
import { api } from '@/client/api';

/** Investors (mandate §14) — the model half of the composed Investors screen. The package left this route a
 *  stub that "explicitly invites" a composed screen; the server has owned the whole flow (grant with a
 *  one-time link token, revoke, terms, payments) with 12 tests behind it, and no UI ever called it.
 *
 *  Two truths the surfaces must keep:
 *  · The grant's link token exists in plaintext EXACTLY ONCE — in the grant response. It is shown once with a
 *    copy control and never stored in model state beyond that showing; the list view has no token because the
 *    server keeps only the hash. A replayed grant returns token:null and the UI says so instead of minting a
 *    fake link.
 *  · The grants list is FETCHED server truth. Every mutation re-reads the list rather than patching a local
 *    copy, so status changes (revoked, expired) are the server's words.
 */

const emptyTerms: InvestorTerms = {
  model: 'profit_split',
  profitSplitPct: 20,
  fixedAmount: null,
  sharePct: null,
  notes: '',
};

export function buildInvestors(c: Ctx) {
  const S = c.S;
  const I = S.inv;

  const patch = (part: Partial<NonNullable<typeof I>>) =>
    c.set((st) => (st.inv ? { inv: { ...st.inv, ...part } } : {}));

  const load = async () => {
    // First visit seeds the working state; later calls refresh the lists in place.
    if (!S.inv)
      c.set({
        inv: {
          loading: true,
          error: null,
          grants: [],
          payments: [],
          form: { name: '', email: '', expires: '' },
          busy: false,
          formError: null,
          issued: null,
          revokeConfirm: null,
          terms: null,
        },
      });
    else patch({ loading: true, error: null });
    try {
      const [list, pays] = await Promise.all([api.manage.investors.list(), api.manage.investors.payments()]);
      patch({ loading: false, grants: list.grants, payments: pays.items });
    } catch (error) {
      patch({
        loading: false,
        error: error instanceof Error ? error.message : 'The investor list could not be loaded.',
      });
    }
  };

  const grantGo = async () => {
    if (!I || I.busy) return;
    const name = I.form.name.trim();
    const email = I.form.email.trim();
    if (!name || !email) {
      patch({
        formError: !name ? 'Name the investor.' : 'An email is required — the link goes to a person.',
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      patch({ formError: 'That does not read as an email address.' });
      return;
    }
    const expiresAt = I.form.expires ? `${I.form.expires}T23:59:59.000Z` : null;
    patch({ busy: true, formError: null });
    try {
      const granted = await api.manage.investors.grant({
        idempotencyKey: `grant-${Date.now()}`,
        investorName: name,
        email,
        expiresAt,
      });
      patch({
        busy: false,
        form: { name: '', email: '', expires: '' },
        issued: { grantId: granted.id, token: granted.grantToken, name },
      });
      await load();
      c.flash(`${name} granted viewer access.`, 'pos');
    } catch (error) {
      patch({
        busy: false,
        formError: error instanceof Error ? error.message : 'The grant could not be created.',
      });
    }
  };

  const revokeGo = async (grantId: string, name: string) => {
    if (I?.busy) return;
    patch({ busy: true });
    try {
      await api.manage.investors.revoke({ idempotencyKey: `revoke-${grantId}`, grantId });
      patch({ busy: false, revokeConfirm: null, issued: I?.issued?.grantId === grantId ? null : I?.issued });
      await load();
      c.flash(`${name}'s access revoked. The portal link stops answering.`, 'mut');
    } catch (error) {
      patch({ busy: false });
      c.flash(error instanceof Error ? error.message : 'The grant could not be revoked.', 'neg');
    }
  };

  const termsOpen = async (grantId: string, name: string) => {
    patch({ terms: { grantId, name, draft: emptyTerms, busy: true } });
    try {
      const t = await api.manage.investors.terms({ grantId });
      c.set((st) =>
        st.inv?.terms?.grantId === grantId
          ? { inv: { ...st.inv, terms: { grantId, name, draft: t.terms, busy: false } } }
          : {},
      );
    } catch (error) {
      patch({ terms: null });
      c.flash(error instanceof Error ? error.message : 'The terms could not be loaded.', 'neg');
    }
  };

  const termsSave = async () => {
    const t = I?.terms;
    if (!t || t.busy) return;
    patch({ terms: { ...t, busy: true } });
    try {
      await api.manage.investors.updateTerms({
        idempotencyKey: `terms-${t.grantId}-${Date.now()}`,
        grantId: t.grantId,
        terms: t.draft,
      });
      patch({ terms: null });
      c.flash(`${t.name}'s terms saved.`, 'pos');
    } catch (error) {
      patch({ terms: { ...t, busy: false } });
      c.flash(error instanceof Error ? error.message : 'The terms could not be saved.', 'neg');
    }
  };

  return {
    invLoad: () => void load(),
    invState: I,
    invRows: (I?.grants ?? []).map((g) => ({
      id: g.id,
      name: g.investor.name,
      initials: g.investor.initials,
      status: g.status,
      statusFg:
        g.status === 'active' ? 'var(--pos)' : g.status === 'pending' ? 'var(--amb)' : 'var(--muted2)',
      created: g.createdAt.slice(0, 10),
      expires: g.expiresAt ? g.expiresAt.slice(0, 10) : 'No expiry',
      revocable: g.status === 'active' || g.status === 'pending',
    })),
    invInterests: c.data.core.scoreInvestors.map((iv) => ({
      initials: iv.init,
      name: iv.name,
      offer: iv.offer,
      badge: iv.tag,
      badgeFg: iv.tagCol,
      badgeBg: iv.tagBg,
      act: false,
    })),
    invPayments: (I?.payments ?? []).map((p) => ({
      id: p.id,
      grant: I?.grants.find((g) => g.id === p.grantId)?.investor.name ?? p.grantId,
      amount: `${p.currency} ${p.amount}`,
      status: p.status,
      at: p.at.slice(0, 10),
    })),
    invSetForm: (key: 'name' | 'email' | 'expires', value: string) =>
      c.set((st) =>
        st.inv ? { inv: { ...st.inv, form: { ...st.inv.form, [key]: value }, formError: null } } : {},
      ),
    invGrantGo: grantGo,
    invIssuedDismiss: () => patch({ issued: null }),
    invCopyLink: () => {
      const token = I?.issued?.token;
      if (!token) return;
      const url = `${window.location.origin}/portal?grant=${token}`;
      void navigator.clipboard?.writeText(url).then(
        () => c.flash('Portal link copied — this is the only time it exists in plaintext.', 'mut'),
        () => c.flash(url, 'mut'), // clipboard refused: show it, so the trader can copy by hand
      );
    },
    invRevokeAsk: (id: string, name: string) => patch({ revokeConfirm: { id, name } }),
    invRevokeCancel: () => patch({ revokeConfirm: null }),
    invRevokeGo: revokeGo,
    invTermsOpen: termsOpen,
    invTermsClose: () => patch({ terms: null }),
    invTermsSet: (part: Partial<InvestorTerms>) =>
      c.set((st) =>
        st.inv?.terms
          ? { inv: { ...st.inv, terms: { ...st.inv.terms, draft: { ...st.inv.terms.draft, ...part } } } }
          : {},
      ),
    invTermsSave: termsSave,
  } satisfies Partial<ViewModel>;
}
